// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Hidden from "@insite/mobius/Hidden";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { XNGAGE_CUSTOM_WIDGET } from "@sc/XDL/constants";
import parse from "html-react-parser";
import React from "react";
import styled from "styled-components";

const enum fields {
    video = "video",
    mobileVideo = "mobileVideo",
    title = "title",
    subtitle = "subtitle",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "buttonVariant",
    buttonAlignment = "buttonAlignment",
    openInNewTab = "openInNewTab",
}

interface BackgroundVideoProps extends WidgetProps {
    fields: {
        [fields.video]: string;
        [fields.mobileVideo]: string;
        [fields.title]: string;
        [fields.subtitle]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.buttonAlignment]: string;
        [fields.openInNewTab]: boolean;
    };
}

export interface BackgroundVideoStyles {
    wrapper?: InjectableCss;
    textOverlayWrapper?: InjectableCss;
    textOverlay?: InjectableCss;
    videoContainer?: InjectableCss;
    title?: TypographyPresentationProps;
    subtitle?: TypographyPresentationProps;
    button?: ButtonPresentationProps;
}

export const ButtonWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;

export const backgroundVideoStyles: BackgroundVideoStyles = {};

const BackgroundVideoWidget = ({
    fields: { video, mobileVideo, title, subtitle, buttonLabel, buttonLink, buttonVariant, buttonAlignment, openInNewTab },
}: BackgroundVideoProps) => {
    const styles = backgroundVideoStyles;
    const link = useGetLink(buttonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const handleButtonOnClick = () => {
        if (!link?.url) {
            return;
        }

        if (openInNewTab) {
            window.open(link?.url, "_blank");
            return;
        }

        if (link?.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            history.push(link?.url);
        } else {
            history.push(link?.url);
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.videoContainer}>
                <Hidden below="md">
                    <video autoPlay muted loop playsInline>
                        <source src={video} type="video/mp4" />
                        {translate("Your browser does not support the video tag.")}
                    </video>
                </Hidden>
                <Hidden above="sm">
                    <video autoPlay muted loop playsInline>
                        <source src={mobileVideo} type="video/mp4" />
                        <source src={video} type="video/mp4" />
                        {translate("Your browser does not support the video tag.")}
                    </video>
                </Hidden>
            </StyledWrapper>
            <StyledWrapper {...styles.textOverlayWrapper}>
                <StyledWrapper {...styles.textOverlay}>
                    {title && (
                        <Typography {...styles.title}>
                            {typeof title === "string" ? parse(title, parserOptions) : title}
                        </Typography>
                    )}
                    {subtitle && (
                        <Typography {...styles.subtitle}>
                            {typeof subtitle === "string" ? parse(subtitle, parserOptions) : subtitle}
                        </Typography>
                    )}
                    {buttonLabel && buttonLink && (
                        <ButtonWrapper alignment={buttonAlignment}>
                            <Button
                                {...styles.button}
                                variant={buttonVariant}
                                onClick={handleButtonOnClick}
                                aria-label={buttonLabel}
                            >
                                {buttonLabel}
                            </Button>
                        </ButtonWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export const definition: WidgetDefinition = {
    group: XNGAGE_CUSTOM_WIDGET,
    fieldDefinitions: [
        {
            name: fields.video,
            displayName: "Video",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            fieldType: "General",
            isRequired: true,
        },
        {
            name: fields.mobileVideo,
            displayName: "Mobile Video",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            fieldType: "General",
            isRequired: false,
        },
        {
            name: fields.title,
            displayName: "Title",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
        },
        {
            name: fields.subtitle,
            displayName: "Subtitle",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
        },
        {
            name: fields.buttonLabel,
            displayName: "Button Label",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            isRequired: false,
        },
        {
            name: fields.buttonLink,
            displayName: "Button Link",
            editorTemplate: "LinkField",
            defaultValue: {
                value: "",
                type: "Page",
            },
            fieldType: "General",
        },
        {
            name: fields.buttonVariant,
            displayName: "Button Type",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Primary", value: "primary" },
                { displayName: "Secondary", value: "secondary" },
            ],
            hideEmptyOption: true,
            defaultValue: "primary",
            fieldType: "Contextual",
        },
        {
            name: fields.buttonAlignment,
            displayName: "Button Alignment",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Left", value: "flex-start" },
                { displayName: "Center", value: "center" },
                { displayName: "Right", value: "flex-end" },
            ],
            defaultValue: "flex-start",
            fieldType: "General",
        },
        {
            name: fields.openInNewTab,
            displayName: "Open in New Tab",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: BackgroundVideoWidget,
    definition,
};

export default widgetModule;
